import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { CACHE_KEYS, Investment, useEditInvestment } from "@bn/api/apiv1";
import { SymbolSearch } from "@features/searchSymbol/components/SymbolSearch";
import { GET_INVESTMENT_KEY } from "@api/cacheKeys";
import { LoadingButton } from "@components";
import { EditTabContainer, EditTabActions } from "./EditTabLayout";

/**
 * On desktop the tab has a fixed height, so let the search chain fill the
 * remaining space and let the results list itself scroll (instead of growing
 * the modal or being cut off). The fixed-height tab keeps the modal stable.
 */
const SearchArea = styled.div`
  ${({ theme }) => theme.breakpoints.up("sm")} {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;

    & > div,
    & > div > div {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      margin-bottom: 0;
    }

    & .symbol-results-list {
      flex: 1;
      min-height: 0;
      max-height: none;
    }
  }
`;

export interface Props {
  investment: Investment;
}

export const InvestmentEditQuoteData = ({ investment }: Props) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [symbol, setSymbol] = useState<SymbolWithQuote>();
  const { mutate: update, isPending } = useEditInvestment();

  const handleSaveClick = () => {
    update(
      {
        id: investment.id,
        createOrUpdateInvestmentParams: {
          tickerSymbol: symbol?.quote?.id,
          quoteProvider: symbol?.quoteProvider,
        },
      },
      {
        onSuccess: async () => {
          enqueueSnackbar(
            `${t("investmentEditPage.basicTab.saved")}: ${investment.name}`,
            { autoHideDuration: 3000, variant: "success" }
          );
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: [GET_INVESTMENT_KEY] }),
            queryClient.invalidateQueries({
              queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_INVESTMENTS],
            }),
          ]);
        },
      }
    );
  };

  const selectedSymbolText = symbol?.name
    ? `${t("investmentEditPage.quoteDataTab.selectedText")} ${symbol?.name} ${symbol?.quote?.stockExchangeName ? `(${symbol?.quote?.stockExchangeName})` : ""}`
    : t("investmentEditPage.quoteDataTab.nothingSelectedText");

  return (
    <EditTabContainer>
      <Typography variant="body1" mb={2}>
        {selectedSymbolText}
      </Typography>
      <SearchArea>
        <SymbolSearch
          search={symbol?.name ?? investment.isin ?? investment.name}
          hasQuote={Boolean(symbol?.quote)}
          onSelectSymbol={(value) => setSymbol(value)}
          noManual
        />
      </SearchArea>
      <EditTabActions>
        <LoadingButton
          variant="primary"
          isFetching={isPending}
          disabled={isPending || !symbol}
          onClick={handleSaveClick}
        >
          {t("investmentEditPage.quoteDataTab.saveButton")}
        </LoadingButton>
      </EditTabActions>
    </EditTabContainer>
  );
};
